import {
  faixaDoGiro,
  calcularAnguloExtracao,
  referenciaisDaColuna,
  type EixoColuna,
  type LadoRotacao,
  type ResultadoAngulo,
  type FaixaGiro,
} from '../geometria/anguloExtracao'
import {
  cross,
  dot,
  girarEmTorno,
  grausParaRad,
  radParaGraus,
  limitar,
  multiplicar,
  somar,
  vetor,
  type Vetor3,
} from '../geometria/vetor3'
import { misturarPose, POSE_ALINHADA, POSE_SENTADA, type PoseVitima } from './rigVitima'
import { aberturaNaAltura, type AberturaSaida } from './aberturas'
import { comprimento, normalizar, subtrair } from '../geometria/vetor3'

export type AnguloNominal = 0 | 30 | 60 | 90 | 180
export const ANGULOS_NOMINAIS: readonly AnguloNominal[] = [0, 30, 60, 90]
export const CONVENCOES_ANGULO: readonly AnguloNominal[] = [0, 30, 60, 90, 180]

/**
 * Ângulo da convenção de trabalho.
 *
 * Na ocorrência ninguém mede 64°: trabalha-se com o valor próximo da convenção
 * — porta-mala é 0°, o motorista pela porta atrás dele é 30°, porta traseira oposta 60°,
 * lateral 90°, e para-brisa / frontal 180°. O cálculo exato continua existindo e continua
 * sendo a origem deste número; ele é que é apresentado ao aluno como o valor de trabalho.
 */
export function anguloConvencional(graus: number): AnguloNominal {
  let escolhido: AnguloNominal = CONVENCOES_ANGULO[0]!
  let menorDistancia = Number.POSITIVE_INFINITY
  for (const candidato of CONVENCOES_ANGULO) {
    const distancia = Math.abs(graus - candidato)
    if (distancia < menorDistancia) {
      menorDistancia = distancia
      escolhido = candidato
    }
  }
  return escolhido
}

/**
 * Giro que o corpo executa, em torno da vertical, para acompanhar a rota.
 *
 * Tanto a vítima sentada quanto a deitada giram em torno de +Y — a sentada em
 * torno da própria coluna, a deitada em torno da normal do teto em que está
 * apoiada. O que muda é de ONDE o giro parte:
 *
 * - sentada, a referência é o eixo sagital, e o giro é o ângulo transversal da
 *   convenção (0/30/60/90);
 * - deitada, o eixo sagital é vertical e não tem projeção horizontal; a
 *   referência passa a ser o próprio eixo do corpo, e o giro é o desvio em
 *   relação a ele.
 *
 * A escolha não é uma bandeira de postura: é qual dos dois eixos tem componente
 * horizontal. Isso mantém arco desenhado, número exibido e movimento do corpo
 * sendo a mesma conta em qualquer configuração.
 */
export interface GiroDoCorpo {
  /** referência horizontal de onde o giro parte, normalizada */
  referencia: Vetor3
  /** giro com sinal: negativo gira para o lado esquerdo (+X), positivo para o direito (-X) */
  anguloRad: number
  /** o giro parte do eixo do corpo (vítima deitada) em vez do eixo sagital */
  aoLongoDoCorpo: boolean
}

const MINIMO_HORIZONTAL = 1e-6

const horizontal = (v: Vetor3): Vetor3 => ({ x: v.x, y: 0, z: v.z })

export function giroDoCorpo(eixoColuna: EixoColuna, pontoSaida: Vetor3): GiroDoCorpo {
  const { eixo, eixoSagital } = referenciaisDaColuna(eixoColuna)
  const rumo = horizontal(subtrair(pontoSaida, eixoColuna.baseColuna))
  if (comprimento(rumo) < MINIMO_HORIZONTAL) {
    throw new Error('A rota não tem direção horizontal: não há giro do corpo a medir.')
  }
  const direcao = normalizar(rumo)

  const sagitalHorizontal = horizontal(eixoSagital)
  const aoLongoDoCorpo = Math.abs(eixo.y) < 0.5 || comprimento(sagitalHorizontal) < 1e-3
  const referencia = normalizar(aoLongoDoCorpo ? horizontal(eixo) : sagitalHorizontal)

  const cosseno = limitar(dot(referencia, direcao), -1, 1)
  let anguloRad = Math.acos(cosseno)
  let sinal = cross(referencia, direcao).y >= 0 ? 1 : -1
  if (aoLongoDoCorpo && anguloRad > Math.PI / 2) {
    anguloRad = Math.PI - anguloRad
    sinal = -sinal
  }

  return { referencia, anguloRad: sinal * anguloRad, aoLongoDoCorpo }
}

export interface RotaExtracao {
  id: string
  nome: string
  /**
   * `nominal` são as demonstrações de 0/30/60/90 do plano; `abertura` é a rota
   * real, definida por onde a vítima está e por onde ela sai — nesta o ângulo
   * é consequência, não escolha.
   */
  origem: 'nominal' | 'abertura' | 'livre'
  anguloNominal: AnguloNominal | null
  abertura: AberturaSaida | null
  /** ponto de saída em coordenadas de mundo */
  pontoSaida: Vetor3
  /** polilinha livre desenhada pelo aluno; ausente nas rotas retas tradicionais */
  pontosLivres?: readonly Vetor3[]
  /** resultado devolvido pela função canônica do domínio */
  resultado: ResultadoAngulo
  /** -1 gira para o lado esquerdo (+X) da vítima, +1 para o direito (-X) */
  sentidoRotacao: number
  /** valor de trabalho, aproximado para a convenção; nulo em retirada axial */
  anguloConvencional: AnguloNominal | null
  /** giro do corpo em torno da vertical para acompanhar esta rota */
  giro: GiroDoCorpo
  descricao: string
}

const convencionalDe = (resultado: ResultadoAngulo, aoLongoDoCorpo = false): AnguloNominal | null => {
  if (aoLongoDoCorpo) {
    return resultado.rotaAxial ? 0 : anguloConvencional(resultado.desvioDoEixoGraus)
  }
  return resultado.anguloGraus == null ? null : anguloConvencional(resultado.anguloGraus)
}

/** Sinal da rotação em torno do eixo da coluna: -1 gira para o lado esquerdo (+X). */
const sinalDoLado = (lado: LadoRotacao): number => (lado === 'esquerdo' ? -1 : 1)

export const DISTANCIA_ROTA_PADRAO = 2.4

/**
 * Constrói a rota girando o eixo sagital em torno do eixo da coluna.
 *
 * Ao derivar o ponto de saída dos mesmos vetores que o cálculo usa, o ângulo
 * devolvido por `calcularAnguloExtracao` é exatamente o nominal — o número da
 * tela continua vindo da matemática do domínio, não de um texto fixo.
 */
export function construirRota(
  eixoColuna: EixoColuna,
  anguloNominal: AnguloNominal,
  ladoPretendido: LadoRotacao = 'esquerdo',
  distancia = DISTANCIA_ROTA_PADRAO,
): RotaExtracao {
  const { eixo, eixoSagital } = referenciaisDaColuna(eixoColuna)
  const sagitalHorizontal = horizontal(eixoSagital)
  const aoLongoDoCorpo = Math.abs(eixo.y) < 0.5 || comprimento(sagitalHorizontal) < 1e-3

  const direcao = aoLongoDoCorpo
    ? girarEmTorno(
        vetor(0, 0, -1),
        vetor(0, 1, 0),
        sinalDoLado(ladoPretendido) * grausParaRad(anguloNominal),
      )
    : girarEmTorno(
        eixoSagital,
        eixo,
        sinalDoLado(ladoPretendido) * grausParaRad(anguloNominal),
      )
  const pontoSaida = somar(eixoColuna.baseColuna, multiplicar(direcao, distancia))
  const resultado = calcularAnguloExtracao(eixoColuna, pontoSaida)
  const giro = giroDoCorpo(eixoColuna, pontoSaida)

  return {
    id: `rota${anguloNominal}`,
    nome: `${anguloNominal}°`,
    origem: 'nominal',
    anguloNominal,
    abertura: null,
    pontoSaida,
    resultado,
    sentidoRotacao: sinalDoLado(ladoPretendido),
    anguloConvencional: anguloNominal,
    giro,
    descricao: DESCRICOES[anguloNominal],
  }
}

/** Quanto a rota segue para fora do veículo depois de atravessar a abertura. */
export const DISTANCIA_ALEM_DA_ABERTURA = 1.15

/**
 * Rota real: a vítima sai por uma abertura concreta do veículo.
 *
 * O ângulo NÃO é escolhido — ele é medido entre o eixo sagital da vítima e a
 * direção que leva até a abertura. É por isso que tirar o motorista pela porta
 * dele dá um número bem maior do que tirá-lo pela porta de trás.
 */
export function construirRotaPorAbertura(
  eixoColuna: EixoColuna,
  abertura: AberturaSaida,
  distanciaAlem = DISTANCIA_ALEM_DA_ABERTURA,
): RotaExtracao {
  // o corpo atravessa o vão na altura da base da coluna, seja ela a de uma
  // vítima sentada ou a de uma vítima deitada no teto do veículo capotado. Todo
  // o percurso fica nesse plano — o mesmo que a animação percorre
  const vao = aberturaNaAltura(abertura, eixoColuna.baseColuna.y)
  const rumo = horizontal(subtrair(vao.ponto, eixoColuna.baseColuna))
  if (comprimento(rumo) < MINIMO_HORIZONTAL) {
    throw new Error(`A abertura ${abertura.id} coincide com a posição da vítima.`)
  }
  const direcao = normalizar(rumo)
  const pontoSaida = somar(vao.ponto, multiplicar(direcao, distanciaAlem))
  const resultado = calcularAnguloExtracao(eixoColuna, pontoSaida)
  const giro = giroDoCorpo(eixoColuna, pontoSaida)

  return {
    id: `saida-${abertura.id}`,
    nome: abertura.curto,
    origem: 'abertura',
    anguloNominal: null,
    abertura,
    pontoSaida,
    resultado,
    sentidoRotacao: giro.anguloRad <= 0 ? -1 : 1,
    anguloConvencional: convencionalDe(resultado, giro.aoLongoDoCorpo),
    giro,
    descricao: abertura.id === 'paraBrisa'
      ? 'Saída frontal pelo para-brisa (~180° em oposição à traseira). Dúvida frequente dos alunos: "Se tirar pelo para-brisa é 0°?". Embora a linha sagital seja reta, a doutrina adota a saída traseira como referencial zero (0°), ficando a frontal em 180°. Na prática é inviabilizada pelo choque com volante, painel e motor, além do risco biológico do vidro.'
      : `Retirada pela ${abertura.nome.toLowerCase()}. O ângulo mostrado é o que o corpo precisa girar para acompanhar esta saída.`,
  }
}

/** Constrói uma rota pela polilinha desenhada pelo aluno no plano de apoio. */
export function construirRotaLivre(eixoColuna: EixoColuna, pontos: readonly Vetor3[]): RotaExtracao {
  const base = eixoColuna.baseColuna
  const normalizados = pontos
    .map((p) => ({ x: p.x, y: base.y, z: p.z }))
    .filter((p, i, lista) => i === 0 || comprimento(subtrair(p, lista[i - 1]!)) > 0.04)
  const pontoSaida = normalizados.at(-1)
  if (!pontoSaida || comprimento(horizontal(subtrair(pontoSaida, base))) < 0.18) {
    throw new Error('Desenhe uma trajetória com pelo menos 18 cm a partir da vítima.')
  }
  const resultado = calcularAnguloExtracao(eixoColuna, pontoSaida)
  const giro = giroDoCorpo(eixoColuna, pontoSaida)
  return {
    id: 'rota-livre',
    nome: 'Trajeto livre',
    origem: 'livre',
    anguloNominal: null,
    abertura: null,
    pontoSaida,
    pontosLivres: [base, ...normalizados],
    resultado,
    sentidoRotacao: giro.anguloRad <= 0 ? -1 : 1,
    anguloConvencional: convencionalDe(resultado, giro.aoLongoDoCorpo),
    giro,
    descricao: 'Trajetória desenhada pelo aluno. O corpo percorre cada trecho do traçado.',
  }
}

/**
 * Constrói a rota a partir da direção contínua da seta direcional interativa.
 * O ângulo se forma continuamente de 0° (traseira) a 180° (para-brisa / frontal).
 */
export function construirRotaDirecional(
  eixoColuna: EixoColuna,
  direcao: Vetor3,
  distancia = DISTANCIA_ROTA_PADRAO,
): RotaExtracao {
  const dirNorm = normalizar(horizontal(direcao))
  const pontoSaida = somar(eixoColuna.baseColuna, multiplicar(dirNorm, distancia))
  const resultado = calcularAnguloExtracao(eixoColuna, pontoSaida)
  const giro = giroDoCorpo(eixoColuna, pontoSaida)
  const angulo = resultado.anguloGraus ?? 0

  const ehFrontal = angulo > 165
  const descricao = ehFrontal
    ? `Trajetória frontal (${Math.round(angulo)}°). Dúvida frequente dos alunos: "Se tirar pelo para-brisa é 0°?". Na doutrina veicular, 0° é a retirada axial traseira; o para-brisa fica no extremo oposto (180°). É inviável na prática pelo choque mecânico com volante, painel e cofre do motor.`
    : `Trajetória orientada pela seta direcional: ${Math.round(angulo)}° em relação ao alinhamento sagital longitudinal.`

  return {
    id: 'rota-direcional',
    nome: `${Math.round(angulo)}°`,
    origem: 'livre',
    anguloNominal: null,
    abertura: null,
    pontoSaida,
    resultado,
    sentidoRotacao: giro.anguloRad <= 0 ? -1 : 1,
    anguloConvencional: convencionalDe(resultado, giro.aoLongoDoCorpo),
    giro,
    descricao,
  }
}

/**
 * Lado nominal da retirada segundo a doutrina de salvamento veicular.
 * Para ocupantes dianteiros (motorista ou passageiro):
 * - 0°: neutro / tampa traseira
 * - 30°: porta traseira do próprio lado do ocupante
 * - 60°: porta traseira do lado OPOSTO (diagonal cruzando o habitáculo, evitando a coluna B)
 * - 90°: porta dianteira do próprio lado
 */
export function ladoNominalDaAncora(
  ladoAncora: LadoRotacao | 'central',
  angulo: AnguloNominal,
): LadoRotacao {
  if (ladoAncora === 'central') return 'esquerdo'
  if (angulo === 60) {
    return ladoAncora === 'direito' ? 'esquerdo' : 'direito'
  }
  return ladoAncora
}

/**
 * Retorna a abertura que mais se alinha com a direção fornecida a partir da base da coluna,
 * desde que o desvio angular seja pequeno (cosseno >= 0.82, aprox. <= 35 graus).
 */
export function aberturaMaisAlinhada(
  eixoColuna: EixoColuna,
  direcao: Vetor3,
  aberturas: readonly AberturaSaida[],
): AberturaSaida | null {
  const dirNorm = normalizar(horizontal(direcao))
  let melhor: AberturaSaida | null = null
  let maiorDot = 0.82
  for (const a of aberturas) {
    const vao = horizontal(subtrair(a.ponto, eixoColuna.baseColuna))
    if (comprimento(vao) < 1e-3) continue
    const vaoNorm = normalizar(vao)
    const d = dot(dirNorm, vaoNorm)
    if (d > maiorDot) {
      maiorDot = d
      melhor = a
    }
  }
  return melhor
}

const DESCRICOES: Record<AnguloNominal, string> = {
  0: 'Saída alinhada ao eixo sagital: o corpo sai para trás sem rotação no plano transversal.',
  30: 'Rotação curta do corpo antes da saída.',
  60: 'Rotação intermediária: o aluno já percebe o tronco girando bastante dentro do vão.',
  90: 'Saída lateral: o corpo gira um quarto de volta em relação ao eixo sagital.',
  180: 'Saída frontal em oposição à traseira (180°): alinhada ao eixo sagital no sentido frontal, bloqueada por volante, painel e motor.',
}

export function rotasPadrao(
  eixoColuna: EixoColuna,
  ladoPretendido: LadoRotacao = 'esquerdo',
  distancia = DISTANCIA_ROTA_PADRAO,
): RotaExtracao[] {
  return ANGULOS_NOMINAIS.map((a) => construirRota(eixoColuna, a, ladoPretendido, distancia))
}

const EIXO_DO_GIRO: Vetor3 = { x: 0, y: 1, z: 0 }

/** Abaixo disto o arco é um ponto: não há giro visível para desenhar. */
const GIRO_MINIMO_RAD = 0.01

/**
 * Pontos do arco que ilustra o giro do corpo.
 *
 * Desenhado a partir de `rota.giro` — a mesma referência e o mesmo ângulo que a
 * animação aplica ao corpo. Com a vítima deitada isso importa: o arco do plano
 * transversal ficaria de pé, mostrando 90° para toda saída, enquanto o corpo gira
 * de fato no plano do teto.
 */
export function pontosDoArco(
  rota: RotaExtracao,
  centro: Vetor3,
  raio = 0.55,
  segmentos = 40,
): Vetor3[] {
  const { referencia, anguloRad } = rota.giro
  if (Math.abs(anguloRad) < GIRO_MINIMO_RAD) return []
  return Array.from({ length: segmentos + 1 }, (_, i) => {
    const v = girarEmTorno(referencia, EIXO_DO_GIRO, anguloRad * (i / segmentos))
    return somar(centro, multiplicar(v, raio))
  })
}

/** O que o número exibido para uma rota está medindo. */
export type MedidaDaRota = 'rotacaoTransversal' | 'desvioDoEixo'

export interface LeituraDaRota {
  /** valor exato, em graus */
  exatoGraus: number
  /** valor de trabalho, arredondado para a convenção */
  convencao: AnguloNominal
  medida: MedidaDaRota
  /** a retirada é axial: o corpo sai pelo próprio eixo, sem girar a coluna */
  axial: boolean
  /**
   * Quanto giro esta rota impõe. A convenção 0/30/60/90 categoriza; a faixa
   * qualifica. Vem daqui, e não de um cálculo próprio da UI, porque esta função
   * já é o funil único do número que vale — painel e rótulo 3D leem os dois da
   * mesma fonte.
   */
  faixa: FaixaGiro
}

/**
 * O número de trabalho da rota, e o que ele mede.
 *
 * Existe porque não há UM ângulo que sirva às duas posturas. Sentada, o
 * informativo é a rotação no plano transversal — a convenção 0/30/60/90 que o
 * instrutor usa. Deitada no teto do capotado, essa rotação vale exatamente 90°
 * para TODA saída (o eixo sagital fica vertical e toda rota é horizontal), e o
 * que discrimina é o desvio em relação ao eixo do corpo.
 *
 * A escolha segue o mesmo critério de `giroDoCorpo`, então o número exibido é o
 * mesmo giro que o corpo executa na animação.
 */
export function leituraDaRota(rota: RotaExtracao): LeituraDaRota {
  const { resultado, giro } = rota
  // a faixa acompanha o GIRO que o corpo executa, que é o que a manobra impõe à
  // vítima — o mesmo número que `amostrarRota` usa para girar o boneco
  const giroGraus = Math.abs((giro.anguloRad * 180) / Math.PI)

  if (!giro.aoLongoDoCorpo && resultado.anguloGraus != null) {
    return {
      exatoGraus: resultado.anguloGraus,
      convencao: anguloConvencional(resultado.anguloGraus),
      medida: 'rotacaoTransversal',
      axial: false,
      faixa: faixaDoGiro(giroGraus),
    }
  }
  return {
    exatoGraus: resultado.desvioDoEixoGraus,
    convencao: anguloConvencional(resultado.desvioDoEixoGraus),
    medida: 'desvioDoEixo',
    axial: resultado.rotaAxial,
    faixa: resultado.rotaAxial ? 'axial' : faixaDoGiro(giroGraus),
  }
}

export interface AmostraRota {
  tempo: number
  fase: string
  /** deslocamento do corpo inteiro em relação à âncora, em coordenadas de mundo */
  deslocamento: Vetor3
  /** guinada do corpo em torno do eixo da coluna */
  guinadaRad: number
  /** guinada aplicada somente à prancha na pá de forno. */
  guinadaPranchaRad: number
  /** reclinação do corpo sobre a prancha; 0 quando a retirada é sem prancha */
  reclinacaoRad: number
  fracaoRotacao: number
  fracaoDeslocamento: number
  /** 0 = ainda sentado na âncora, 1 = corpo já alinhado fora do veículo */
  fracaoLevantar: number
  /** quanto da prancha já foi introduzida; 0 quando não há prancha */
  fracaoPrancha: number
  pose: PoseVitima
  /** Fração de colocação do colar cervical (0 = sem colar, 1 = colocado) */
  fracaoColarCervical?: number
  /** Fração de retirada do apoio de cabeça do banco (0 = no lugar, 1 = retirado) */
  fracaoApoioCabeca?: number
  /** Fração de reclinação/abaixamento do banco (0 = em pé, 1 = reclinado/abaixado) */
  fracaoAbaixarBanco?: number
  /**
   * Fração de rebatimento do encosto do banco TRASEIRO, para a frente (0 = em pé,
   * 1 = deitado sobre o assento). Vem antes de `fracaoAbaixarBanco`: primeiro o de
   * trás deita para a frente, depois o da frente reclina para trás — o que dá o
   * espaço a mais na saída pela traseira.
   */
  fracaoRebaterBancosTraseiros?: number
  /** Fração de abertura da tampa traseira / porta-malas (0 = fechada, 1 = aberta) */
  fracaoAberturaTampa?: number
  /** Fração de abertura das portas (0 = fechadas, 1 = abertas) */
  fracaoAberturaPortas?: number
}

/**
 * Fração do giro que fica como TORÇÃO da cervical sobre a pelve, em vez de
 * guinada do corpo inteiro.
 *
 * Até aqui o corpo girava em bloco: `guinadaRad` levava tudo junto, e os graus
 * de liberdade de torção do rig (`troncoTorcaoRad`, `cabecaTorcaoRad`) existiam,
 * eram desenhados por `VitimaProcedural` e nunca eram escritos. O aluno lia "90°"
 * e via o boneco girar inteiro, o que é justamente a manobra CORRETA — e assim
 * não via o que a manobra evita.
 *
 * Distribuir parte do giro como torção residual mostra o custo de NÃO mover o
 * corpo em bloco. A soma guinada + tronco + cabeça continua valendo o giro total,
 * então a direção final do corpo não muda e nada do que já estava calibrado
 * regride: com `fracaoTorcao` em 0 o resultado é idêntico ao de antes.
 */
export const FRACAO_TORCAO_PADRAO = 0.35

/** Como a torção residual se reparte entre tronco e cabeça. */
const PARCELA_TRONCO = 0.4
const PARCELA_CABECA = 0.6

export interface OpcoesRota {
  /** quanto a pelve sobe entre a posição sentada e a posição final da rota */
  elevacaoFinal?: number
  /**
   * Retirada sobre prancha longa: a prancha entra primeiro, a vítima gira,
   * é deitada sobre ela e só então percorre a rota — a sequência da técnica.
   */
  comPrancha?: boolean
  /**
   * A vítima já está deitada antes de a retirada começar — o caso do veículo
   * capotado. Não há o que reclinar nem por que levantar: o corpo já está no
   * plano de saída, e a reclinação vem da colocação, não da rota.
   */
  deitada?: boolean
  /**
   * Técnica da pá de forno para vítima já deitada: elevar o corpo, introduzir a
   * prancha, abaixar sobre ela e só então retirar o conjunto.
   */
  paDeForno?: {
    /** direção local que corresponde a subir contra a gravidade */
    direcaoElevacao: Vetor3
    /** distância de elevação suficiente para a prancha entrar */
    altura: number
  }
  /**
   * Quanto do giro fica na cervical em vez de ir para a guinada do bloco.
   * 0 = corpo girado em bloco, que é a manobra correta e o comportamento
   * histórico. Padrão `FRACAO_TORCAO_PADRAO`.
   */
  fracaoTorcao?: number
}

const INICIO_ROTACAO = 0.12
/** Instante em que a rotação do corpo termina; exportado para medir o giro. */
export const FIM_ROTACAO = 0.5
/** quanto do percurso o corpo já vence enquanto ainda gira */
const AVANCO_DURANTE_ROTACAO = 0.16
/** elevação máxima da pelve ao transpor a soleira */
const ELEVACAO_SOLEIRA = 0.12

/** Fases da sequência com prancha, em fração do tempo total. */
export const PRANCHA = {
  abrirPortas: [0, 0.12],
  /** só na saída pela traseira: o banco de trás deita para a frente antes de o da frente reclinar */
  rebaterBancosTraseiros: [0.06, 0.18],
  colarCervical: [0.10, 0.20],
  apoioCabeca: [0.18, 0.30],
  abaixarBanco: [0.28, 0.40],
  entrada: [0.40, 0.56],
  rotacao: [0.54, 0.70],
  reclinacao: [0.68, 0.82],
  deslocamento: [0.82, 1],
} as const

/** Fases da pá de forno, em fração do tempo total. */
export const PA_DE_FORNO = {
  abrirTampa: [0, 0.12],
  /** o banco de trás deita para a frente antes de o da frente reclinar (`abaixarBanco`) */
  rebaterBancosTraseiros: [0.04, 0.16],
  colarCervical: [0, 0.10],
  apoioCabeca: [0.10, 0.22],
  abaixarBanco: [0.20, 0.32],
  elevar: [0.32, 0.46],
  inserirPrancha: [0.44, 0.60],
  abaixar: [0.58, 0.70],
  alinhar: [0.68, 0.78],
  retirar: [0.76, 1],
} as const

/**
 * Pontos pedagógicos usados pelos controles anterior/próximo.
 *
 * A timeline continua contínua e arrastável; estes marcos apenas dão ao
 * professor saltos coerentes com a técnica que está sendo demonstrada. Assim
 * o mesmo controle guiado atende retirada livre, prancha e pá de forno.
 */
export const MARCOS_RETIRADA = {
  livre: [0, INICIO_ROTACAO, FIM_ROTACAO, 1],
  prancha: [
    0,
    PRANCHA.abrirPortas[1],
    PRANCHA.colarCervical[1],
    PRANCHA.apoioCabeca[1],
    PRANCHA.abaixarBanco[1],
    PRANCHA.entrada[1],
    PRANCHA.rotacao[1],
    PRANCHA.reclinacao[1],
    1,
  ],
  paDeForno: [
    0,
    PA_DE_FORNO.abrirTampa[1],
    PA_DE_FORNO.apoioCabeca[1],
    PA_DE_FORNO.abaixarBanco[1],
    PA_DE_FORNO.elevar[1],
    PA_DE_FORNO.inserirPrancha[1],
    PA_DE_FORNO.abaixar[1],
    PA_DE_FORNO.alinhar[1],
    1,
  ],
} as const

export function marcosDaRetirada(paDeForno: boolean, comPrancha: boolean): readonly number[] {
  if (paDeForno) return MARCOS_RETIRADA.paDeForno
  return comPrancha ? MARCOS_RETIRADA.prancha : MARCOS_RETIRADA.livre
}

/** Reclinação final do corpo deitado sobre a prancha. */
export const RECLINACAO_DEITADA = 1.32

/** Comprimento de uma prancha longa de imobilização, em metros. */
export const COMPRIMENTO_PRANCHA = 1.85

const suavizar = (t: number): number => t * t * (3 - 2 * t)

const faixa = (t: number, inicio: number, fim: number): number =>
  limitar((t - inicio) / (fim - inicio), 0, 1)

/** Posição horizontal ao longo da rota, distribuída pelo comprimento real da polilinha. */
export function pontoNaRota(rota: RotaExtracao, eixoColuna: EixoColuna, fracao: number): Vetor3 {
  const pontos = rota.pontosLivres?.length
    ? rota.pontosLivres
    : [eixoColuna.baseColuna, rota.pontoSaida]
  if (pontos.length < 2) return eixoColuna.baseColuna
  const comprimentos = pontos.slice(1).map((p, i) => comprimento(horizontal(subtrair(p, pontos[i]!))))
  const total = comprimentos.reduce((soma, valor) => soma + valor, 0)
  if (total < MINIMO_HORIZONTAL) return eixoColuna.baseColuna
  let alvo = limitar(fracao, 0, 1) * total
  for (let i = 0; i < comprimentos.length; i += 1) {
    const trecho = comprimentos[i]!
    if (alvo <= trecho || i === comprimentos.length - 1) {
      const inicio = pontos[i]!
      const fim = pontos[i + 1]!
      const local = trecho < MINIMO_HORIZONTAL ? 0 : limitar(alvo / trecho, 0, 1)
      return {
        x: inicio.x + (fim.x - inicio.x) * local,
        y: eixoColuna.baseColuna.y,
        z: inicio.z + (fim.z - inicio.z) * local,
      }
    }
    alvo -= trecho
  }
  return rota.pontoSaida
}

/**
 * Amostra a animação de retirada.
 *
 * Sem prancha, a vítima gira — para o aluno VER os graus acontecendo — e depois
 * percorre a rota de pé. Com prancha, a sequência é a da técnica: prancha,
 * rotação, deitar, deslocar.
 */
export function amostrarRota(
  rota: RotaExtracao,
  eixoColuna: EixoColuna,
  tempo: number,
  opcoes: OpcoesRota = {},
): AmostraRota {
  const {
    elevacaoFinal = 0,
    comPrancha = false,
    deitada = false,
    paDeForno,
    fracaoTorcao = comPrancha || paDeForno ? 0 : FRACAO_TORCAO_PADRAO,
  } = opcoes
  const torcao = limitar(fracaoTorcao, 0, 1)
  const t = limitar(tempo, 0, 1)
  const saidaPelaTraseira = rota.abertura?.id === 'tampaTraseira' || rota.anguloNominal === 0
  // o giro vem da rota, não de `anguloGraus`: deitada, o ângulo transversal fica
  // preso em 90° e giraria o corpo muito além do que a manobra exige
  const total = rota.giro.anguloRad
  // a guinada leva o que NÃO sobrou na cervical; a soma continua sendo `total`
  const totalGuinada = total * (1 - torcao)

  /** Pose com a torção residual daquele instante escrita nos eixos do rig. */
  const comTorcao = (pose: PoseVitima, fracao: number): PoseVitima => {
    if (torcao < 1e-9) return pose
    const residual = total * torcao * fracao
    return {
      ...pose,
      troncoTorcaoRad: pose.troncoTorcaoRad + residual * PARCELA_TRONCO,
      cabecaTorcaoRad: pose.cabecaTorcaoRad + residual * PARCELA_CABECA,
    }
  }
  const deslocamentoNaRota = (fracao: number): Vetor3 =>
    subtrair(pontoNaRota(rota, eixoColuna, fracao), eixoColuna.baseColuna)

  if (paDeForno) {
    const fracaoAberturaTampa = suavizar(
      faixa(t, PA_DE_FORNO.abrirTampa[0], PA_DE_FORNO.abrirTampa[1]),
    )
    const fracaoColarCervical = suavizar(
      faixa(t, PA_DE_FORNO.colarCervical[0], PA_DE_FORNO.colarCervical[1]),
    )
    const fracaoApoioCabeca = suavizar(
      faixa(t, PA_DE_FORNO.apoioCabeca[0], PA_DE_FORNO.apoioCabeca[1]),
    )
    const fracaoAbaixarBanco = suavizar(
      faixa(t, PA_DE_FORNO.abaixarBanco[0], PA_DE_FORNO.abaixarBanco[1]),
    )
    const fracaoRebaterBancosTraseiros = suavizar(
      faixa(t, PA_DE_FORNO.rebaterBancosTraseiros[0], PA_DE_FORNO.rebaterBancosTraseiros[1]),
    )
    const subida = suavizar(faixa(t, PA_DE_FORNO.elevar[0], PA_DE_FORNO.elevar[1]))
    const descida = suavizar(faixa(t, PA_DE_FORNO.abaixar[0], PA_DE_FORNO.abaixar[1]))
    const fracaoElevada = subida * (1 - descida)
    const fracaoPrancha = suavizar(
      faixa(t, PA_DE_FORNO.inserirPrancha[0], PA_DE_FORNO.inserirPrancha[1]),
    )
    const fracaoRotacao = suavizar(
      faixa(t, PA_DE_FORNO.alinhar[0], PA_DE_FORNO.alinhar[1]),
    )
    const fracaoDeslocamento = suavizar(
      faixa(t, PA_DE_FORNO.retirar[0], PA_DE_FORNO.retirar[1]),
    )
    const direcao = normalizar(paDeForno.direcaoElevacao)
    const elevacao = multiplicar(direcao, paDeForno.altura * fracaoElevada)

    return {
      tempo: t,
      fase: fasePaDeForno(t, saidaPelaTraseira),
      deslocamento: somar(deslocamentoNaRota(fracaoDeslocamento), elevacao),
      guinadaRad: 0,
      guinadaPranchaRad: total * fracaoRotacao,
      reclinacaoRad: 0,
      fracaoRotacao,
      fracaoDeslocamento,
      fracaoLevantar: fracaoElevada,
      fracaoPrancha,
      pose: POSE_ALINHADA,
      fracaoAberturaTampa,
      fracaoAberturaPortas: fracaoAberturaTampa,
      fracaoColarCervical,
      fracaoApoioCabeca,
      fracaoAbaixarBanco,
      fracaoRebaterBancosTraseiros,
    }
  }

  if (comPrancha) {
    const fracaoAberturaPortas = suavizar(faixa(t, PRANCHA.abrirPortas[0], PRANCHA.abrirPortas[1]))
    const fracaoAberturaTampa = suavizar(faixa(t, PRANCHA.abrirPortas[0], PRANCHA.abrirPortas[1]))
    const fracaoColarCervical = deitada ? 1 : suavizar(faixa(t, PRANCHA.colarCervical[0], PRANCHA.colarCervical[1]))
    const fracaoApoioCabeca = deitada ? 1 : suavizar(faixa(t, PRANCHA.apoioCabeca[0], PRANCHA.apoioCabeca[1]))
    const fracaoAbaixarBanco = deitada ? 1 : suavizar(faixa(t, PRANCHA.abaixarBanco[0], PRANCHA.abaixarBanco[1]))
    const fracaoRebaterBancosTraseiros = deitada
      ? 1
      : suavizar(faixa(t, PRANCHA.rebaterBancosTraseiros[0], PRANCHA.rebaterBancosTraseiros[1]))
    const fracaoPrancha = suavizar(faixa(t, PRANCHA.entrada[0], PRANCHA.entrada[1]))
    const fracaoRotacao = suavizar(faixa(t, PRANCHA.rotacao[0], PRANCHA.rotacao[1]))
    const fracaoReclinar = suavizar(faixa(t, PRANCHA.reclinacao[0], PRANCHA.reclinacao[1]))
    const fracaoDeslocamento = suavizar(faixa(t, PRANCHA.deslocamento[0], PRANCHA.deslocamento[1]))

    return {
      tempo: t,
      fase: faseComPrancha(t, deitada, saidaPelaTraseira),
      // deitado sobre a prancha o corpo não sobe: sai na altura dela, e a
      // prancha acompanha o deslocamento — vítima e prancha saem juntas
      deslocamento: deslocamentoNaRota(fracaoDeslocamento),
      guinadaRad: total * fracaoRotacao,
      guinadaPranchaRad: total * fracaoRotacao,
      // já deitada, não há reclinação a executar: ela vem da colocação no teto
      reclinacaoRad: deitada ? 0 : RECLINACAO_DEITADA * fracaoReclinar,
      fracaoRotacao,
      fracaoDeslocamento,
      fracaoLevantar: deitada ? 1 : fracaoReclinar,
      fracaoPrancha,
      pose: deitada ? POSE_ALINHADA : misturarPose(POSE_SENTADA, POSE_ALINHADA, fracaoReclinar),
      fracaoAberturaPortas,
      fracaoAberturaTampa,
      fracaoColarCervical,
      fracaoApoioCabeca,
      fracaoAbaixarBanco,
      fracaoRebaterBancosTraseiros,
    }
  }

  const fracaoRotacao = suavizar(faixa(t, INICIO_ROTACAO, FIM_ROTACAO))
  const avanco = AVANCO_DURANTE_ROTACAO * fracaoRotacao
  const restante = (1 - AVANCO_DURANTE_ROTACAO) * suavizar(faixa(t, FIM_ROTACAO - 0.1, 1))
  const fracaoDeslocamento = limitar(avanco + restante, 0, 1)
  const fracaoLevantar = deitada ? 1 : suavizar(faixa(t, 0.45, 1))
  // deitada no teto o corpo desliza: não transpõe soleira de pé nem se levanta
  const soleira = deitada
    ? 0
    : ELEVACAO_SOLEIRA * Math.sin(Math.PI * limitar(fracaoDeslocamento / 0.6, 0, 1))
  const elevacao = deitada ? 0 : soleira + elevacaoFinal * fracaoLevantar

  return {
    tempo: t,
    fase: faseDaRota(t, deitada),
    deslocamento: somar(deslocamentoNaRota(fracaoDeslocamento), { x: 0, y: elevacao, z: 0 }),
    guinadaRad: totalGuinada * fracaoRotacao,
    guinadaPranchaRad: totalGuinada * fracaoRotacao,
    reclinacaoRad: 0,
    fracaoRotacao,
    fracaoDeslocamento,
    fracaoLevantar,
    fracaoPrancha: 0,
    pose: comTorcao(
      deitada ? POSE_ALINHADA : misturarPose(POSE_SENTADA, POSE_ALINHADA, fracaoLevantar),
      fracaoRotacao,
    ),
    fracaoAberturaPortas: suavizar(faixa(t, 0, 0.12)),
    fracaoAberturaTampa: suavizar(faixa(t, 0, 0.12)),
  }
}

function faseComPrancha(t: number, deitada: boolean, traseira = false): string {
  if (deitada) {
    if (t < PRANCHA.entrada[1]) return 'Prancha sendo posicionada'
    if (t < PRANCHA.rotacao[1]) return 'Alinhamento do corpo à saída'
    if (t < PRANCHA.reclinacao[1]) return 'Transferência para a prancha'
    if (t < 0.98) return 'Deslocamento sobre a prancha'
    return 'Posição final'
  }
  if (traseira) {
    if (t < PRANCHA.rebaterBancosTraseiros[0]) return 'Abertura das portas e do porta-malas'
    if (t < PRANCHA.rebaterBancosTraseiros[1]) return 'Rebatendo o encosto do banco traseiro para a frente'
  } else if (t < PRANCHA.abrirPortas[1]) {
    return 'Abertura das portas para acesso'
  }
  if (t < PRANCHA.colarCervical[1]) return 'Colocação do colar cervical'
  if (t < PRANCHA.apoioCabeca[1]) return 'Retirando apoio de cabeça do banco'
  if (t < PRANCHA.abaixarBanco[1]) return 'Abaixando encosto do banco'
  if (t < PRANCHA.entrada[1]) return 'Introdução da prancha pelo ângulo de saída'
  if (t < PRANCHA.rotacao[1]) return 'Alinhamento da vítima'
  if (t < PRANCHA.reclinacao[1]) return 'Colocação da vítima na prancha'
  if (t < 0.98) return 'Deslocamento sobre a prancha'
  return 'Posição final'
}

function fasePaDeForno(t: number, traseira = false): string {
  if (traseira && t >= PA_DE_FORNO.rebaterBancosTraseiros[0] && t < PA_DE_FORNO.rebaterBancosTraseiros[1]) {
    return 'Rebatendo o encosto do banco traseiro para a frente'
  }
  if (t < PA_DE_FORNO.abrirTampa[1]) return 'Abertura dos acessos (portas e porta-malas)'
  if (t < PA_DE_FORNO.apoioCabeca[1]) return 'Remoção dos encostos de cabeça'
  if (t < PA_DE_FORNO.abaixarBanco[1]) return 'Reclinação dos bancos'
  if (t < PA_DE_FORNO.elevar[1]) return 'Pá de forno · elevando a vítima'
  if (t < PA_DE_FORNO.inserirPrancha[1]) return 'Pá de forno · introduzindo a prancha'
  if (t < PA_DE_FORNO.abaixar[1]) return 'Pá de forno · abaixando sobre a prancha'
  if (t < PA_DE_FORNO.alinhar[1]) return 'Pá de forno · alinhando para a saída'
  if (t < 0.98) return 'Pá de forno · retirando pela traseira'
  return 'Posição final'
}

function faseDaRota(t: number, deitada = false): string {
  if (t < INICIO_ROTACAO) return deitada ? 'Posição inicial no teto' : 'Posição inicial na âncora'
  if (t < FIM_ROTACAO) return deitada ? 'Alinhamento do corpo à saída' : 'Rotação do corpo'
  if (t < 0.98) return 'Deslocamento pela rota'
  return 'Posição final'
}

/**
 * Trajetória percorrida pela base da coluna, para desenhar a rota completa.
 * É a mesma amostragem usada na animação, então linha e corpo não divergem.
 */
export function pontosDaTrajetoria(
  rota: RotaExtracao,
  eixoColuna: EixoColuna,
  segmentos = 36,
  opcoes: OpcoesRota = {},
): Vetor3[] {
  return Array.from({ length: segmentos + 1 }, (_, i) =>
    somar(eixoColuna.baseColuna, amostrarRota(rota, eixoColuna, i / segmentos, opcoes).deslocamento),
  )
}
